import { addToast, ColumnTable, Icon, Loading, Table } from '@octano/global-ui';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from 'reactstrap';
import DisplayError from '../../../../../components/info/DisplayError';
import { useActionKitImp } from '../../../../../components/ozmoit/common';
import { TranslationsKeys } from '../../../../../locales/translations';
import { getStudentObservationDocumentDownloadAction } from '../actions';
import { ObservationTypes } from '../constanst/types-observations';
import { StudentObservationsDataLoader } from '../Loaders/StudentObservationsLoader';
import { StudentObservationsListResponse } from '../types';
import { StudentObservationModalMethods } from './StudentObservationCreateModal';

const StudentObservationsTable = ({
  refModal,
}: {
  refModal: React.RefObject<StudentObservationModalMethods>;
}) => {
  const { t } = useTranslation(TranslationsKeys.STUDENT_FILE_OBSERVATIONS);
  const {
    loading,
    query,
    result,
    updateQuery,
  } = StudentObservationsDataLoader();

  const { action: handleClickDownload } = useActionKitImp(
    getStudentObservationDocumentDownloadAction,
    {
      onSuccess: () => {
        addToast({
          color: 'success',
          text: t('list.msg.downloadSuccess'),
        });
      },
      onError: {
        unknown: () => {
          addToast({
            color: 'danger',
            text: t('list.msg.downloadError'),
          });
        },
      },
    },
  );

  const see = useCallback(
    (row: StudentObservationsListResponse) => {
      refModal?.current?.open(row, true);
    },
    [refModal],
  );

  const getUrl = useCallback(
    async (params: { studentId: number; observationId: number }) => {
      const data = await handleClickDownload(params);
      if (data.success) {
        window.open(data.data.url, '_blank');
      }
    },
    [handleClickDownload],
  );

  const columns = useMemo<ColumnTable<StudentObservationsListResponse>[]>(
    () => [
      {
        columnName: 'type',
        tdClassName: 'text-center',
        thClassName: 'text-center bg-secondary',
        headerText: t(`list.table.headers.type`),
        cellFormat: ({ row }) => {
          return ObservationTypes[row.type];
        },
      },
      {
        columnName: 'course',
        tdClassName: 'text-center',
        thClassName: 'text-center bg-secondary',
        headerText: t(`list.table.headers.course`),
        cellFormat: ({ value }) => {
          return value?.name ?? 'N/A';
        },
      },
      {
        columnName: 'createdAt',
        tdClassName: 'text-center',
        thClassName: 'text-center bg-secondary',
        headerText: t(`list.table.headers.createdAt`),
        cellFormat: ({ value }) => {
          const date = dayjs(value);
          return date.format('DD/MM/YYYY');
        },
      },
      {
        columnName: 'observation',
        width: '45%',
        thClassName: 'text-center bg-secondary',
        headerText: t(`list.table.headers.observation`),
        cellFormat: ({ value }) => {
          if (!value) {
            return '-';
          }
          return (
            <div
              style={{
                maxWidth: '450px',
                textAlign: 'justify',
                padding: '8px',
                margin: '0 auto',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
              }}
            >
              {value}
            </div>
          );
        },
      },
      {
        columnName: 'createdBy',
        tdClassName: 'text-center',
        thClassName: 'text-center bg-secondary',
        headerText: t(`list.table.headers.createdBy`),
        cellFormat: ({ row }) => <span>{row?.createdBy?.name || '-'}</span>,
      },
      {
        width: '10%',
        columnName: 'file',
        thClassName: 'text-center bg-secondary',
        tdClassName: 'text-center',
        headerText: t(`list.table.headers.file`),
        cellFormat({ row }) {
          const docId = row?.file?.id;
          return (
            docId && (
              <button
                type="button"
                className="btn"
                onClick={() =>
                  getUrl({ studentId: row.studentId, observationId: row.id })
                }
                disabled={loading}
              >
                <Icon name="download" />
              </button>
            )
          );
        },
      },
      {
        columnName: 'action',
        tdClassName: 'text-center',
        thClassName: 'text-center bg-secondary',
        headerText: t(`list.table.headers.action`),
        cellFormat: ({ row }) => (
          <div>
            <button type="button" className="btn" onClick={() => see(row)}>
              <Icon name="eye" />
            </button>
          </div>
        ),
      },
    ],
    [t, see, getUrl, loading],
  );

  if (!result?.success && loading) {
    return (
      <Card>
        <Loading textBody={''} />
      </Card>
    );
  }

  if (!result?.success) {
    return (
      <DisplayError
        insideCard
        title={t(`list.queryError.title`)}
        textBody={t(`list.queryError.description`)}
      />
    );
  }

  return (
    <div className="w-100 mt-4 d-block">
      <Table
        data={result?.data?.data ?? []}
        columns={columns}
        isLoadingResults={loading}
        pagination={{
          itemsPerPage: query.items_per_page,
          currentPage: query.page,
          totalItems: result.data.total,
          totalPages: result.data.total_pages,
          onChangePage: (nextPage) => {
            updateQuery({
              ...query,
              page: nextPage,
            });
          },
        }}
      />
    </div>
  );
};

export default StudentObservationsTable;
